/**
 * MAHI — Login (returning users).
 *
 * Mobile number is the account's unique identity, so login is phone-only —
 * one OTP to the number used at signup. New users are routed to
 * /mira/welcome. Google sign-in available as an alternative.
 *
 * Endpoints:
 *   POST /api/mira/auth/login/start    { identifier }
 *   POST /api/mira/auth/login/verify   { identifier, code }
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import mahi, { errMsg, trackEvent } from '../api';
import { useMahiAuth } from '../auth';
import { warmDiscoverFeed } from '../prefetch';
import MahiRobot, { useMahiMood } from '../components/MahiRobot';
import MahiLogo from '../components/ui/MahiLogo';
import MahiShell from '../components/MahiShell';
import MahiFashionBackdrop from '../components/MahiFashionBackdrop';
import GoogleSignInButton from '../components/GoogleSignInButton';

export default function MahiLogin() {
  const navigate = useNavigate();
  const { refresh } = useMahiAuth();
  const [phone, setPhone] = useState('');
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const { mood, setMood, flash } = useMahiMood();

  const send = async () => {
    const v = phone.trim();
    if (!v) return toast.error('Enter your mobile number');
    setBusy(true);
    setMood('thinking');
    try {
      await mahi.post('/auth/login/start', { identifier: v });
      setSent(true);
      trackEvent('login_started', { channel: 'phone' });
      toast.success('OTP sent to your mobile');
      flash('celebrating');
    } catch (e) { toast.error(errMsg(e, 'Could not send OTP')); flash('alert'); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (!code) return toast.error('Enter the OTP');
    setBusy(true);
    setMood('thinking');
    try {
      const { data } = await mahi.post('/auth/login/verify', {
        identifier: phone.trim(), code,
      });
      localStorage.setItem('mira_token', data.token);
      // Login was the one authenticated entry point that never warmed the
      // feed — every returning user who signed out, or landed here with an
      // expired token, paid a fully serial fetch on arrival at /discover.
      // warmDiscoverFeed picks the gender out of the cached user when this
      // device has seen them before.
      warmDiscoverFeed();
      trackEvent('login_completed', { channel: 'phone' });
      flash('celebrating');
      const user = await refresh();
      const step = user?.onboarding_step ?? 1;
      navigate(step >= 5 ? '/discover' : `/onboarding/${step}`);
    } catch (e) { toast.error(errMsg(e, 'Verification failed')); flash('alert'); }
    finally { setBusy(false); }
  };

  const googleSignIn = async (credential) => {
    setBusy(true);
    setMood('thinking');
    try {
      const { data } = await mahi.post('/auth/google', { credential });
      localStorage.setItem('mira_token', data.token);
      warmDiscoverFeed();
      trackEvent('login_completed', { channel: 'google' });
      flash('celebrating');
      const user = await refresh();
      const step = user?.onboarding_step ?? 1;
      navigate(step >= 5 ? '/discover' : `/onboarding/${step}`);
    } catch (e) { toast.error(errMsg(e, 'Google sign-in failed')); flash('alert'); }
    finally { setBusy(false); }
  };

  return (
    <MahiShell>
      <div className="min-h-screen flex items-center justify-center px-5 py-12 relative">
        <MahiFashionBackdrop />
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="w-full max-w-md relative z-10"
        >
          <div className="text-center mb-10">
            <div className="flex justify-center mb-5"><MahiRobot size={96} mood={mood} /></div>
            <div className="flex justify-center text-slate-700">
              <MahiLogo className="h-[18px] w-auto" decorative />
            </div>
            <h1 className="mt-2 font-mahi-serif text-4xl text-slate-900 tracking-tight">
              Welcome back
            </h1>
            <p className="mt-2 text-sm text-slate-500 font-mahi-sans">Sign in to continue.</p>
          </div>

          <div className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.18)] p-7">
            {!sent ? (
              <>
                <label className="block text-xs font-medium text-slate-500 mb-2 font-mahi-sans">Mobile number</label>
                <div className="relative mb-4">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    data-testid="mahi-login-identifier-input" type="tel" inputMode="tel"
                    enterKeyHint="go"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ''))}
                    className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 font-mahi-sans focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-300"
                    placeholder="+91 98765 43210"
                    autoComplete="tel"
                    autoFocus
                  />
                </div>
                <p className="text-[11px] text-slate-400 mb-4 font-mahi-sans">
                  We&apos;ll send an OTP to your mobile.
                </p>

                <button
                  data-testid="mahi-login-send-otp-btn"
                  onClick={send} disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-6 py-3.5 font-medium font-mahi-sans hover:bg-slate-800 transition disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send OTP <ArrowRight className="w-4 h-4" /></>}
                </button>

                <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-mahi-sans">
                  <span className="flex-1 h-px bg-slate-100" /> or <span className="flex-1 h-px bg-slate-100" />
                </div>
                <GoogleSignInButton onCredential={googleSignIn} />
              </>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-1 font-mahi-sans">
                  OTP sent to <span className="font-medium text-slate-700">{phone}</span>
                </p>
                <p className="text-[11px] text-slate-400 mb-5 font-mahi-sans">
                  Enter the code we just sent to your mobile.
                </p>

                <label className="block text-xs font-medium text-slate-500 mb-2 font-mahi-sans">OTP</label>
                {/* autoComplete="one-time-code" is what makes iOS offer the SMS
                    straight from the keyboard bar (and Android autofill it)
                    rather than making someone leave the app, memorise four
                    digits and come back. type=tel, not number: number renders a
                    spinner and drops leading zeros. */}
                <input
                  data-testid="mahi-login-otp-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  type="tel"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  enterKeyHint="go"
                  maxLength={4}
                  className="w-full text-center tracking-[0.4em] text-2xl font-semibold rounded-xl border border-slate-200 px-4 py-3.5 mb-5 font-mahi-sans focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-300"
                  placeholder="••••"
                  autoFocus
                />

                <button
                  data-testid="mahi-login-verify-btn"
                  onClick={verify} disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-6 py-3.5 font-medium font-mahi-sans hover:bg-slate-800 transition disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Sign In'}
                </button>
                <button
                  data-testid="mahi-login-edit-identifier-btn"
                  onClick={() => { setSent(false); setCode(''); }}
                  className="w-full mt-3 text-xs text-slate-400 hover:text-slate-600 font-mahi-sans"
                >Use a different mobile number</button>
              </>
            )}
          </div>

          <p className="text-center text-sm text-slate-500 mt-6 font-mahi-sans">
            New to MAHI?{' '}
            <Link
              data-testid="mahi-go-to-signup-link"
              to="/signup"
              className="font-medium text-slate-900 hover:text-cyan-600 transition"
            >Create an account</Link>
          </p>
          <p className="text-center text-xs text-slate-400 mt-3 font-mahi-sans">
            By continuing you agree to MAHI&apos;s{' '}
            <Link to="/terms" className="underline hover:text-slate-600">Terms</Link>{' '}
            and{' '}
            <Link to="/privacy-policy" className="underline hover:text-slate-600">Privacy Policy</Link>.
            A NanoKart product.
          </p>
        </motion.div>
      </div>
    </MahiShell>
  );
}
